/**
 * AuthGate — route-level guard that renders children only when authenticated.
 *
 * Responsibilities:
 *  - If AUTHENTICATED → render <Outlet /> (protected routes)
 *  - If UNINITIALIZED / RESTORING / AUTHENTICATING → render <AuthInitializer />
 *  - If ERROR / UNAUTHENTICATED → the AuthInitializer already navigated to /unauthorized
 *
 * Also runs `useInitializeAppData()` to warm up caches once authenticated
 * (same behavior as the old AuthWrapper).
 *
 * Replaces: AuthWrapper + InitApplicationAuthState in pages/auth/auth.tsx
 */

import { Navigate, Outlet } from "react-router-dom"

import { SpinnerScreen } from "@be-miniapp/ui/components/ui/spinner"

import { useInitializeAppData } from "@/hooks/use-initialize-app-data"

import { getUnauthorizedPath } from "@/constants/auth-error"

import { AuthInitializer } from "./auth-initializer"
import { useAuthStore } from "./auth-store"
import { AUTH_STATUS } from "./auth-types"

function AuthGate() {
  const status = useAuthStore((s) => s.status)
  const errorCode = useAuthStore((s) => s.error?.code)

  // Warm up app data caches once authenticated
  useInitializeAppData()

  if (status === AUTH_STATUS.AUTHENTICATED) {
    return <Outlet />
  }

  if (status === AUTH_STATUS.ERROR) {
    return <Navigate to={getUnauthorizedPath(errorCode)} replace />
  }

  if (status === AUTH_STATUS.UNAUTHENTICATED) {
    return <Navigate to="/unauthorized" replace />
  }

  // For all in-flight states, mount the initializer and keep a visible loader.
  return (
    <>
      <AuthInitializer />
      <SpinnerScreen />
    </>
  )
}

AuthGate.displayName = "AuthGate"

export { AuthGate }
